/**
 * SuperAdmin Users — Sprint 12-S9
 * Cross-tenant user search with role filter, pagination, and debounced search bar.
 * GET /api/superadmin/users
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FiUsers, FiSearch, FiRefreshCw, FiEdit2, FiUserPlus } from 'react-icons/fi';
import { getSuperAdminUsers, impersonateUserSuperAdmin, updateSuperAdminUser, getSuperAdminOwnerById, removeOwnerCentre, addOwnerCentre, getSuperAdminTenants, getUserCentreRoles, addUserCentreRole, removeUserCentreRole, createSuperAdminOwner, assignUserAsOwner, softDropHealerFromCentre, overrideHealerDropCooldown, assignPatientToTenant } from '../../utils/api';
import colors from '../../styles/colors';

// Inject responsive CSS once on first render
const STYLE_ID = 'sa-users-responsive';
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes sa-u-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
    .sa-u-pulse { animation: sa-u-pulse 1.4s ease-in-out infinite; }
    @media (max-width: 600px) { .sa-u-hide-mobile { display: none !important; } }
  `;
  document.head.appendChild(el);
}

const ROLE_BADGE = {
  patient:        { bg: colors.statusInfoLight,      color: colors.statusInfo      },
  healer:         { bg: colors.statusSuccessLight,    color: colors.statusSuccess   },
  admin:          { bg: colors.statusWarningLight,    color: colors.statusWarning   },
  centreAdmin:    { bg: colors.statusWarningLight,    color: colors.statusWarning   },
  globalOrgAdmin: { bg: colors.brandSecondaryLight,   color: colors.brandSecondary  },
  foundationAdmin:{ bg: colors.brandPrimaryLight,     color: colors.brandPrimaryDark},
  owner:          { bg: colors.brandSecondaryLight,   color: colors.brandSecondary  },
  student:        { bg: colors.backgroundSecondary,   color: colors.textSecondary   },
  general:        { bg: colors.backgroundSecondary,   color: colors.textSecondary   },
};

// Mirrors User.js role enum minus 'superadmin' — superadmin rows show no Edit button.
const ROLE_OPTIONS = [
  '', 'admin', 'centreAdmin', 'healer', 'patient',
  'owner', 'foundationAdmin', 'globalOrgAdmin', 'student', 'general',
];

function RoleBadge({ role }) {
  const style = ROLE_BADGE[role] || { bg: colors.backgroundSecondary, color: colors.textSecondary };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: '600',
      background: style.bg,
      color: style.color,
      whiteSpace: 'nowrap',
    }}>
      {role}
    </span>
  );
}

function StatusBadge({ isActive }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: '600',
      background: isActive ? colors.statusSuccessLight : colors.statusDangerLight,
      color:      isActive ? colors.statusSuccess      : colors.statusDanger,
    }}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {[120, 160, 100, 90, 80, 70].map((w, i) => (
        <td key={i} style={{ padding: '12px 14px' }}>
          <div className="sa-u-pulse" style={{
            height: '14px', borderRadius: '6px', width: `${w}px`,
            background: colors.backgroundSecondary,
          }} />
        </td>
      ))}
    </tr>
  );
}

const S = {
  wrap:    { padding: '0' },
  toolbar: { display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' },
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: colors.backgroundCard, border: `1px solid ${colors.borderDefault}`,
    borderRadius: '8px', padding: '0 12px', flex: '1', minWidth: '180px', maxWidth: '340px',
  },
  searchIcon: { color: colors.textMuted, flexShrink: 0 },
  searchInput: {
    border: 'none', outline: 'none', background: 'transparent',
    fontSize: '13px', color: colors.textPrimary, width: '100%', padding: '9px 0',
  },
  select: {
    border: `1px solid ${colors.borderDefault}`, borderRadius: '8px',
    padding: '9px 12px', fontSize: '13px', color: colors.textPrimary,
    background: colors.backgroundCard, cursor: 'pointer', minWidth: '130px',
  },
  card: {
    background: colors.backgroundCard, borderRadius: '12px',
    border: `1px solid ${colors.borderDefault}`, overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '11px 14px', fontSize: '11px', fontWeight: '700', textAlign: 'left',
    color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px',
    background: colors.backgroundPage, borderBottom: `1px solid ${colors.borderDefault}`,
  },
  td: {
    padding: '12px 14px', fontSize: '13px', color: colors.textPrimary,
    borderBottom: `1px solid ${colors.borderDefault}`,
    verticalAlign: 'middle',
  },
  name:    { fontWeight: '600', color: colors.textPrimary },
  email:   { color: colors.textSecondary, fontSize: '12px', marginTop: '2px' },
  empty: {
    padding: '52px 20px', textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
  },
  emptyIcon: { fontSize: '36px', color: colors.textMuted },
  emptyText: { fontSize: '14px', color: colors.textMuted },
  error: {
    padding: '32px 20px', textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
  },
  errorText: { fontSize: '14px', color: colors.statusDanger },
  retryBtn: {
    padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    background: colors.brandPrimary, color: colors.textOnBrand,
    fontSize: '13px', fontWeight: '600',
  },
  footer: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderTop: `1px solid ${colors.borderDefault}`,
    flexWrap: 'wrap', gap: '8px',
  },
  footerCount: { fontSize: '13px', color: colors.textMuted },
  pageBtn: (disabled) => ({
    padding: '6px 14px', borderRadius: '7px', border: `1px solid ${colors.borderDefault}`,
    background: disabled ? colors.backgroundSecondary : colors.backgroundCard,
    color: disabled ? colors.disabledText : colors.textPrimary,
    cursor: disabled ? 'default' : 'pointer', fontSize: '13px',
  }),
  editBtn:       { padding: '6px 12px', borderRadius: '7px', border: `1px solid ${colors.borderDefault}`,
                   background: colors.backgroundCard, color: colors.textPrimary,
                   cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                   display: 'flex', alignItems: 'center', gap: '4px' },
  overlay:       { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                   display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:         { background: colors.backgroundCard, borderRadius: '14px', padding: '28px 28px 24px',
                   width: '100%', maxWidth: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', margin: '16px' },
  modalTitle:    { margin: '0 0 4px', fontSize: '17px', fontWeight: '700', color: colors.textPrimary },
  modalSub:      { margin: '0 0 20px', fontSize: '12px', color: colors.textMuted },
  fieldLabel:    { display: 'block', fontSize: '12px', fontWeight: '600',
                   color: colors.textSecondary, marginBottom: '6px' },
  toggleRow:     { display: 'flex', gap: '8px', marginBottom: '16px' },
  toggleBtn:     (active) => ({
                   flex: 1, padding: '9px', borderRadius: '8px',
                   border: `1px solid ${active ? colors.brandPrimary : colors.borderDefault}`,
                   background: active ? colors.brandPrimary : colors.backgroundCard,
                   color: active ? colors.textOnBrand : colors.textSecondary,
                   cursor: 'pointer', fontSize: '13px', fontWeight: '600' }),
  notesInput:    { width: '100%', border: `1px solid ${colors.borderDefault}`, borderRadius: '8px',
                   padding: '9px 12px', fontSize: '13px', color: colors.textPrimary,
                   background: colors.backgroundCard, resize: 'vertical', marginBottom: '16px',
                   boxSizing: 'border-box', fontFamily: 'inherit' },
  ownRoleWarning:{ fontSize: '12px', color: colors.statusWarning, marginTop: '-10px', marginBottom: '12px' },
  ownerBtn:      { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '6px', border: `1px solid ${colors.brandPrimary}`, background: colors.backgroundCard, color: colors.brandPrimary, fontSize: '11px', fontWeight: '600', cursor: 'pointer', minHeight: '28px', whiteSpace: 'nowrap' },
  modalFooter:   { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' },
  cancelBtn:     { padding: '9px 20px', borderRadius: '8px', border: `1px solid ${colors.borderDefault}`,
                   background: colors.backgroundCard, color: colors.textSecondary,
                   cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  saveBtn:       { padding: '9px 20px', borderRadius: '8px', border: 'none',
                   background: colors.brandPrimary, color: colors.textOnBrand,
                   cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
};

const Users = () => {
  const [search,   setSearch]   = useState('');
  const [role,     setRole]     = useState('');
  const [page,     setPage]     = useState(1);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const [editUser,      setEditUser]      = useState(null);
  const [editForm,      setEditForm]      = useState({ isActive: true, role: '', notes: '' });
  const [editError,     setEditError]     = useState(null);
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [ownerTenants,        setOwnerTenants]        = useState([]);
  const [ownerTenantsLoading, setOwnerTenantsLoading] = useState(false);
  const [ownerRemoveLoading,  setOwnerRemoveLoading]  = useState(null);
  const [allTenants,          setAllTenants]          = useState([]);
  const [addCentreId,         setAddCentreId]         = useState('');
  const [addCentreLoading,    setAddCentreLoading]    = useState(false);
  const [ownerConfirm,        setOwnerConfirm]        = useState(null);
  const [centreRoles,         setCentreRoles]         = useState([]);
  const [crLoading,           setCrLoading]           = useState(false);
  const [addCrTenantId,       setAddCrTenantId]       = useState('');
  const [addCrRole,           setAddCrRole]           = useState('healer');
  const [addCrOpLoading,      setAddCrOpLoading]      = useState(false);
  const [crRemoveLoading,     setCrRemoveLoading]     = useState(null);
  const [crConfirm,           setCrConfirm]           = useState(null);
  const [dropRemarksUser,     setDropRemarksUser]     = useState(null);
  const [dropRemarksText,     setDropRemarksText]     = useState('');
  const [overrideLoading,     setOverrideLoading]     = useState(null);
  const [showCreateOwner,    setShowCreateOwner]    = useState(false);
  const [ownerForm,          setOwnerForm]          = useState({ firstName: '', lastName: '', email: '' });
  const [ownerError,         setOwnerError]         = useState('');
  const [ownerSubmitting,    setOwnerSubmitting]    = useState(false);
  const [assignOwnerConfirm, setAssignOwnerConfirm] = useState(null);
  const [assignOwnerLoading, setAssignOwnerLoading] = useState(false);
  const [assignTenantId,    setAssignTenantId]    = useState('');
  const [assignLoading,     setAssignLoading]     = useState(false);
  const [assignError,       setAssignError]       = useState(null);
  const [assignSuccess,     setAssignSuccess]     = useState(null);
  const [assignConfirm,     setAssignConfirm]     = useState(null);
  const currentUserId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('pwc_user'))?._id || ''; }
    catch { return ''; }
  }, []);

  const debounceRef   = useRef(null);
  const hasMountedRef = useRef(false);
  const searchRef     = useRef(search);
  searchRef.current   = search;

  const fetchUsers = useCallback(async (searchVal, roleVal, pageVal) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: pageVal, limit: 20 };
      if (searchVal && searchVal.trim().length >= 2) params.search = searchVal.trim();
      if (roleVal) params.role = roleVal;
      const res = await getSuperAdminUsers(params);
      // Guard: backend returns { users: [...], total, ... }; handle legacy flat-array shape
      const raw = res.data.data;
      setData(Array.isArray(raw) ? { users: raw, total: raw.length, totalPages: 1 } : raw);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    // Skip initial mount — Effect below [page] owns the first fetch.
    // fetchUsers is a stable useCallback ref; without this guard it double-fetches on mount.
    if (!hasMountedRef.current) { hasMountedRef.current = true; return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (search.trim().length > 0 && search.trim().length < 2) return;
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchUsers(search, role, 1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [search, role, fetchUsers]);

  useEffect(() => {
    fetchUsers(search, role, page);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleRoleChange = (e) => {
    setRole(e.target.value);
    setPage(1);
  };

  const handleRetry = () => fetchUsers(search, role, page);

  const handleOpenEdit = async (u) => {
    setEditUser(u);
    setEditForm({ isActive: u.isActive, role: u.role, notes: u.adminNotes || '' });
    setEditError(null);
    setOwnerTenants([]);
    setAllTenants([]);
    setCentreRoles([]);
    setAddCrTenantId('');
    setAddCrRole('healer');
    setCrConfirm(null);
    setDropRemarksUser(null);
    setDropRemarksText('');
    setOverrideLoading(null);

    const isSuperAdmin = u.role === 'superadmin';

    // Fetch tenants for both owner centre picker and centreRoles picker
    if (!isSuperAdmin) {
      try {
        const tenRes = await getSuperAdminTenants({ status: 'active', limit: 200 });
        setAllTenants(tenRes.data.data || []);
      } catch (_err) { /* non-fatal */ }
    }

    if (u.role === 'owner') {
      setOwnerTenantsLoading(true);
      try {
        const res = await getSuperAdminOwnerById(u._id);
        setOwnerTenants(res.data.data?.ownedTenants || []);
      } catch (_err) {
        // non-fatal — owners section shows empty
      } finally {
        setOwnerTenantsLoading(false);
      }
    }

    if (!isSuperAdmin) {
      setCrLoading(true);
      try {
        const res = await getUserCentreRoles(u._id);
        setCentreRoles(res.data.data || []);
      } catch (_err) { /* non-fatal */ }
      finally { setCrLoading(false); }
    }
  };

  const handleCloseEdit = () => {
    if (isSubmitting) return;
    setEditUser(null);
    setEditError(null);
    setOwnerTenants([]);
    setOwnerRemoveLoading(null);
    setAllTenants([]);
    setAddCentreId('');
    setOwnerConfirm(null);
    setCentreRoles([]);
    setAddCrTenantId('');
    setAddCrRole('healer');
    setCrRemoveLoading(null);
    setCrConfirm(null);
    setDropRemarksUser(null);
    setDropRemarksText('');
    setOverrideLoading(null);
    setAssignTenantId('');
    setAssignLoading(false);
    setAssignError(null);
    setAssignSuccess(null);
    setAssignConfirm(null);
  };

  const handleSaveEdit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setEditError(null);
    try {
      const payload = { isActive: editForm.isActive };
      if (editUser._id !== currentUserId) payload.role = editForm.role;
      payload.notes = editForm.notes.trim();  // always send; empty string clears existing adminNotes
      const res = await updateSuperAdminUser(editUser._id, payload);
      const saved = res.data.data;
      setData(prev => prev
        ? { ...prev, users: prev.users.map(u => u._id === saved._id ? saved : u) }
        : prev
      );
      setEditUser(null);
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to save changes.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveOwnerTenant = (tenantId, tenantName) => {
    if (ownerRemoveLoading) return;
    setOwnerConfirm({
      title: 'Remove centre?',
      body: `Remove ${tenantName} from this owner's centres? They will lose access to owner-level features for this centre.`,
      confirmLabel: 'Remove',
      onConfirm: async () => {
        setOwnerRemoveLoading(String(tenantId));
        try {
          const res = await removeOwnerCentre(editUser._id, tenantId);
          setOwnerTenants(prev => prev.filter(t => String(t._id) !== String(tenantId)));
          if (res.data?.data?.roleDowngraded) {
            // Role changed owner → healer — update user list and close modal
            setData(prev => prev
              ? { ...prev, users: prev.users.map(u => u._id === editUser._id ? { ...u, role: 'healer' } : u) }
              : prev
            );
            setEditUser(null);
          }
        } catch (err) {
          setEditError(err.response?.data?.message || 'Failed to remove centre.');
        } finally {
          setOwnerRemoveLoading(null);
          setOwnerConfirm(null);
        }
      },
    });
  };

  const handleAddOwnerTenant = () => {
    if (!addCentreId || addCentreLoading) return;
    const tenant = allTenants.find(t => String(t._id) === String(addCentreId));
    setOwnerConfirm({
      title: 'Assign centre?',
      body: `Assign ${tenant?.tenantName || 'this centre'} to this owner? They will gain full owner-level access.`,
      confirmLabel: 'Assign',
      onConfirm: async () => {
        setAddCentreLoading(true);
        try {
          await addOwnerCentre(editUser._id, addCentreId);
          if (tenant) setOwnerTenants(prev => [...prev, { _id: tenant._id, tenantName: tenant.tenantName, city: tenant.city || null }]);
          setAddCentreId('');
        } catch (err) {
          setEditError(err.response?.data?.message || 'Failed to assign centre.');
        } finally {
          setAddCentreLoading(false);
          setOwnerConfirm(null);
        }
      },
    });
  };

  const handleRemoveCentreRole = (tenantId, role, centreName) => {
    if (crRemoveLoading) return;
    setCrConfirm({
      title: 'Remove centre role?',
      body: `Remove ${role} role at ${centreName}? The user will lose that context.`,
      confirmLabel: 'Remove',
      onConfirm: async () => {
        setCrRemoveLoading(`${tenantId}_${role}`);
        try {
          await removeUserCentreRole(editUser._id, tenantId, role);
          setCentreRoles(prev => prev.filter(r => !(String(r.tenantId) === String(tenantId) && r.role === role)));
        } catch (err) {
          setEditError(err.response?.data?.message || 'Failed to remove role.');
        } finally {
          setCrRemoveLoading(null);
          setCrConfirm(null);
        }
      },
    });
  };

  const handleAddCentreRole = () => {
    if (!addCrTenantId || !addCrRole || addCrOpLoading) return;
    const tenant = allTenants.find(t => String(t._id) === String(addCrTenantId));
    setCrConfirm({
      title: 'Add centre role?',
      body: `Add ${addCrRole} role at ${tenant?.tenantName || 'this centre'}?`,
      confirmLabel: 'Add',
      onConfirm: async () => {
        setAddCrOpLoading(true);
        try {
          await addUserCentreRole(editUser._id, addCrTenantId, addCrRole);
          setCentreRoles(prev => [
            ...prev.filter(r => !(String(r.tenantId) === String(addCrTenantId) && r.role === addCrRole)),
            { tenantId: addCrTenantId, centreName: tenant?.tenantName || '', role: addCrRole, active: true },
          ]);
          setAddCrTenantId('');
          setAddCrRole('healer');
        } catch (err) {
          setEditError(err.response?.data?.message || 'Failed to add role.');
        } finally {
          setAddCrOpLoading(false);
          setCrConfirm(null);
        }
      },
    });
  };

  const handleSoftDropHealer = async () => {
    if (!dropRemarksUser || crRemoveLoading) return;
    if (dropRemarksText.trim().length < 10) return;
    setCrRemoveLoading(dropRemarksUser.key);
    try {
      await softDropHealerFromCentre(editUser._id, dropRemarksUser.tenantId, dropRemarksText.trim());
      setCentreRoles(prev => prev.map(r =>
        String(r.tenantId) === String(dropRemarksUser.tenantId) && r.role === 'healer'
          ? { ...r, active: false, droppedAt: new Date().toISOString(), cooldownOverridden: false }
          : r
      ));
      setDropRemarksUser(null);
      setDropRemarksText('');
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to drop healer.');
    } finally {
      setCrRemoveLoading(null);
    }
  };

  const handleOverrideCooldown = async (tenantId, centreName, key) => {
    if (overrideLoading) return;
    setOverrideLoading(key);
    try {
      await overrideHealerDropCooldown(editUser._id, tenantId);
      setCentreRoles(prev => prev.map(r =>
        String(r.tenantId) === String(tenantId) && r.role === 'healer'
          ? { ...r, cooldownOverridden: true }
          : r
      ));
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to override cooldown.');
    } finally {
      setOverrideLoading(null);
    }
  };

  async function handleCreateOwner() {
    setOwnerError('');
    if (!ownerForm.firstName.trim()) { setOwnerError('First name is required.'); return; }
    if (!ownerForm.email.trim())     { setOwnerError('Email is required.');      return; }
    setOwnerSubmitting(true);
    try {
      await createSuperAdminOwner(ownerForm);
      setShowCreateOwner(false);
      setOwnerForm({ firstName: '', lastName: '', email: '' });
      fetchUsers(search, role, page);
    } catch (err) {
      setOwnerError(err.response?.data?.message || 'Failed to create owner.');
    } finally {
      setOwnerSubmitting(false);
    }
  }

  const handleAssignAsOwner = (u) => {
    setAssignOwnerConfirm({
      user: u,
      onConfirm: async () => {
        setAssignOwnerLoading(true);
        try {
          await assignUserAsOwner(u._id);
          setData(prev => prev
            ? { ...prev, users: prev.users.map(row => row._id === u._id ? { ...row, role: 'owner' } : row) }
            : prev
          );
        } catch (err) {
          alert(err.response?.data?.message || 'Failed to promote user to owner.');
        } finally {
          setAssignOwnerLoading(false);
          setAssignOwnerConfirm(null);
        }
      },
    });
  };

  const handleImpersonate = async (u) => {
    try {
      const res = await impersonateUserSuperAdmin(u._id);
      const token = res.data.token || res.data.data?.token;
      const url = window.location.origin + '/impersonate?t=' +
        encodeURIComponent(token) + '&role=' + encodeURIComponent(u.role) +
        '&name=' + encodeURIComponent(u.firstName || '');
      window.open(url, '_blank');
    } catch (e) {
      alert(e.response?.data?.message || 'Could not impersonate');
    }
  };

  const handleAssignPatient = () => {
    if (!assignTenantId || assignLoading) return;
    const selectedTenant = allTenants.find(t => String(t._id) === assignTenantId);
    setAssignConfirm({
      tenantName: selectedTenant?.tenantName || assignTenantId,
      onConfirm: async () => {
        setAssignLoading(true);
        setAssignError(null);
        setAssignConfirm(null);
        try {
          const res = await assignPatientToTenant(editUser._id, assignTenantId);
          setAssignSuccess(
            res.data.mobileVerified
              ? 'Patient assigned successfully.'
              : 'Patient assigned. No phone on file — patient should update their profile.'
          );
          setData(prev => prev
            ? { ...prev, users: prev.users.map(u =>
                u._id === editUser._id
                  ? { ...u, tenantId: assignTenantId, tenantName: selectedTenant?.tenantName || '', tenantCity: selectedTenant?.city || '' }
                  : u
              )}
            : prev
          );
          fetchUsers(search, role, page);
        } catch (err) {
          setAssignError(err.response?.data?.message || err.response?.data?.error || 'Assignment failed. Please try again.');
        } finally {
          setAssignLoading(false);
        }
      },
    });
  };

  const { users = [], total = 0, totalPages = 1 } = data || {};
  const from = total === 0 ? 0 : (page - 1) * 20 + 1;
  const to   = Math.min(page * 20, total);

  return (
    <div style={S.wrap}>
      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <FiSearch style={S.searchIcon} size={15} />
          <input
            style={S.searchInput}
            type="text"
            placeholder="Search name or email…"
            value={search}
            onChange={handleSearchChange}
            aria-label="Search users"
          />
        </div>
        <select
          style={S.select}
          value={role}
          onChange={handleRoleChange}
          aria-label="Filter by role"
        >
          {ROLE_OPTIONS.map(r => (
            <option key={r} value={r}>{r ? r : 'All roles'}</option>
          ))}
        </select>
        <button
          style={{ ...S.pageBtn(false), display: 'flex', alignItems: 'center', gap: '5px' }}
          onClick={handleRetry}
          type="button"
          title="Refresh"
          aria-label="Refresh"
        >
          <FiRefreshCw size={13} />
        </button>
        <button
          type="button"
          onClick={() => { setOwnerForm({ firstName: '', lastName: '', email: '' }); setOwnerError(''); setShowCreateOwner(true); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', borderRadius: '8px', border: 'none',
            background: colors.brandPrimary, color: '#fff',
            fontWeight: '600', fontSize: '13px', cursor: 'pointer',
          }}
        >
          <FiUserPlus size={15} /> Create Owner
        </button>
      </div>

      <div style={S.card}>
        {error ? (
          <div style={S.error}>
            <p style={S.errorText}>{error}</p>
            <button style={S.retryBtn} onClick={handleRetry} type="button">Retry</button>
          </div>
        ) : (
          <>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Name / Email</th>
                  <th style={S.th}>Role</th>
                  <th style={S.th}>Status</th>
                  <th style={{ ...S.th }} className="sa-u-hide-mobile">Centre</th>
                  <th style={{ ...S.th }} className="sa-u-hide-mobile">Joined</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                  : users.length === 0
                    ? (
                      <tr>
                        <td colSpan={6}>
                          <div style={S.empty}>
                            <FiUsers style={S.emptyIcon} />
                            <span style={S.emptyText}>No users found</span>
                          </div>
                        </td>
                      </tr>
                    )
                    : users.map(u => (
                      <tr key={u._id}>
                        <td style={S.td}>
                          <div style={S.name}>{u.firstName} {u.lastName}</div>
                          <div style={S.email}>{u.email}</div>
                        </td>
                        <td style={S.td}><RoleBadge role={u.role} /></td>
                        <td style={S.td}><StatusBadge isActive={u.isActive} /></td>
                        <td style={{ ...S.td, color: colors.textSecondary }} className="sa-u-hide-mobile">
                          {u.role === 'owner'
                            ? <span style={{ color: colors.textSecondary }}>Owns {u.ownedTenantCount || 0} centre{u.ownedTenantCount !== 1 ? 's' : ''}</span>
                            : u.tenantName
                              ? <>{u.tenantName}{u.tenantCity ? <span style={{ color: colors.textMuted }}> · {u.tenantCity}</span> : null}</>
                              : <span style={{ color: colors.textMuted }}>—</span>}
                        </td>
                        <td style={{ ...S.td, color: colors.textMuted, whiteSpace: 'nowrap' }} className="sa-u-hide-mobile">
                          {u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {u.role !== 'superadmin' && (
                              <button
                                style={S.editBtn}
                                onClick={() => handleOpenEdit(u)}
                                type="button"
                                title="Edit user"
                                aria-label="Edit user"
                              >
                                <FiEdit2 size={12} />
                                Edit
                              </button>
                            )}
                            {!['superadmin', 'admin', 'owner'].includes(u.role) && (
                              <button
                                style={S.ownerBtn}
                                onClick={() => handleAssignAsOwner(u)}
                                type="button"
                                title="Promote to owner"
                              >
                                <FiUserPlus size={12} />
                                Make Owner
                              </button>
                            )}
                            {!['superadmin', 'admin', 'globalOrgAdmin'].includes(u.role) && (
                              <button
                                style={S.retryBtn}
                                onClick={() => handleImpersonate(u)}
                                type="button"
                              >
                                Login as
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>

            <div style={S.footer}>
              <span style={S.footerCount}>
                {loading ? 'Loading…' : total === 0 ? '0 users' : `${from}–${to} of ${total} users`}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={S.pageBtn(page <= 1 || loading)}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  type="button"
                >
                  Prev
                </button>
                <button
                  style={S.pageBtn(page >= totalPages || loading)}
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages || loading}
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {editUser && (
        <div style={S.overlay} onClick={handleCloseEdit}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h3 style={S.modalTitle}>Edit User</h3>
            <p style={S.modalSub}>{editUser.firstName} {editUser.lastName} · {editUser.email}</p>

            <label style={S.fieldLabel}>Status</label>
            <div style={S.toggleRow}>
              <button
                style={S.toggleBtn(editForm.isActive === true)}
                onClick={() => setEditForm(f => ({ ...f, isActive: true }))}
                type="button"
              >
                Active
              </button>
              <button
                style={S.toggleBtn(editForm.isActive === false)}
                onClick={() => setEditForm(f => ({ ...f, isActive: false }))}
                type="button"
              >
                Inactive
              </button>
            </div>

            <label style={S.fieldLabel}>Role</label>
            <select
              style={{ ...S.select, width: '100%', marginBottom: '16px', boxSizing: 'border-box' }}
              value={editForm.role}
              onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
              disabled={editUser._id === currentUserId}
            >
              {ROLE_OPTIONS.filter(r => r !== '').map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {editUser._id === currentUserId && (
              <p style={S.ownRoleWarning}>You cannot change your own role.</p>
            )}

            <label style={S.fieldLabel}>Notes (optional)</label>
            <textarea
              style={S.notesInput}
              value={editForm.notes}
              onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Internal note about this user…"
              rows={3}
            />

            {editUser.role !== 'superadmin' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={S.fieldLabel}>Centre Roles</label>
                {crLoading
                  ? <div style={{ fontSize: '12px', color: colors.textMuted }}>Loading…</div>
                  : centreRoles.length === 0
                    ? <div style={{ fontSize: '12px', color: colors.textMuted }}>No centre roles assigned</div>
                    : centreRoles.map(r => {
                        const key = `${r.tenantId}_${r.role}`;
                        const isDropped = r.role === 'healer' && !r.active && r.droppedAt;
                        return (
                          <div key={key} style={{ marginBottom: '8px', borderRadius: '8px', border: `1px solid ${colors.borderDefault}`, padding: '8px 10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, fontSize: '13px', color: colors.textPrimary }}>
                                {r.centreName || String(r.tenantId)}
                                <span style={{ marginLeft: '6px', padding: '2px 7px', borderRadius: '8px', background: colors.backgroundSecondary, fontSize: '11px', color: colors.textSecondary, fontWeight: '600' }}>{r.role}</span>
                                {isDropped && (
                                  <span style={{ marginLeft: '6px', padding: '2px 7px', borderRadius: '8px', background: '#fff3cd', fontSize: '11px', color: '#856404', fontWeight: '600' }}>Dropped</span>
                                )}
                              </div>
                              {isDropped && !r.cooldownOverridden && (
                                <button
                                  type="button"
                                  disabled={overrideLoading === key}
                                  onClick={() => handleOverrideCooldown(r.tenantId, r.centreName, key)}
                                  style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #f0ad4e', background: '#fff3cd', color: '#856404', fontSize: '11px', fontWeight: '600', cursor: overrideLoading === key ? 'not-allowed' : 'pointer', minHeight: '28px', whiteSpace: 'nowrap' }}
                                >
                                  {overrideLoading === key ? '…' : 'Override 90-day cooldown'}
                                </button>
                              )}
                              {r.active && r.role === 'healer' && (
                                <button
                                  type="button"
                                  disabled={!!crRemoveLoading}
                                  onClick={() => { setDropRemarksUser({ tenantId: r.tenantId, centreName: r.centreName, key }); setDropRemarksText(''); }}
                                  style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${colors.statusDanger}`, background: colors.backgroundCard, color: colors.statusDanger, fontSize: '11px', fontWeight: '600', cursor: crRemoveLoading ? 'not-allowed' : 'pointer', minHeight: '28px' }}
                                >
                                  Drop
                                </button>
                              )}
                              {r.active && r.role !== 'healer' && (
                                <button
                                  type="button"
                                  disabled={!!crRemoveLoading}
                                  onClick={() => handleRemoveCentreRole(r.tenantId, r.role, r.centreName)}
                                  style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${colors.statusDanger}`, background: colors.backgroundCard, color: colors.statusDanger, fontSize: '11px', fontWeight: '600', cursor: crRemoveLoading ? 'not-allowed' : 'pointer', minHeight: '28px' }}
                                >
                                  {crRemoveLoading === key ? '…' : 'Remove'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                }
                {allTenants.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                    <select
                      value={addCrTenantId}
                      onChange={e => setAddCrTenantId(e.target.value)}
                      style={{ flex: 2, minWidth: '120px', padding: '8px 10px', borderRadius: '7px', border: `1px solid ${colors.borderDefault}`, fontSize: '12px', color: colors.textPrimary, minHeight: '36px' }}
                    >
                      <option value="">Centre…</option>
                      {allTenants.map(t => <option key={t._id} value={t._id}>{t.tenantName}</option>)}
                    </select>
                    <select
                      value={addCrRole}
                      onChange={e => setAddCrRole(e.target.value)}
                      style={{ flex: 1, minWidth: '90px', padding: '8px 10px', borderRadius: '7px', border: `1px solid ${colors.borderDefault}`, fontSize: '12px', color: colors.textPrimary, minHeight: '36px' }}
                    >
                      {['healer', 'centreAdmin', 'admin', 'student'].map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button
                      type="button"
                      disabled={!addCrTenantId || addCrOpLoading}
                      onClick={handleAddCentreRole}
                      style={{ padding: '8px 14px', borderRadius: '7px', border: 'none', background: (addCrTenantId && !addCrOpLoading) ? colors.brandPrimary : colors.textMuted, color: '#fff', fontSize: '12px', fontWeight: '700', cursor: (addCrTenantId && !addCrOpLoading) ? 'pointer' : 'default', minHeight: '36px', whiteSpace: 'nowrap' }}
                    >
                      {addCrOpLoading ? '…' : 'Add Role'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {editForm.role === 'owner' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={S.fieldLabel}>Owned Centres</label>
                {ownerTenantsLoading
                  ? <div style={{ fontSize: '12px', color: colors.textMuted }}>Loading…</div>
                  : ownerTenants.length === 0
                    ? <div style={{ fontSize: '12px', color: colors.textMuted }}>No centres assigned</div>
                    : ownerTenants.map(t => (
                        <div key={t._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <div style={{ flex: 1, fontSize: '13px', color: colors.textPrimary }}>
                            {t.tenantName}
                            {t.city ? <span style={{ color: colors.textMuted }}> · {t.city}</span> : null}
                          </div>
                          <button
                            type="button"
                            disabled={!!ownerRemoveLoading}
                            onClick={() => handleRemoveOwnerTenant(t._id, t.tenantName)}
                            style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${colors.statusDanger}`, background: colors.backgroundCard, color: colors.statusDanger, fontSize: '11px', fontWeight: '600', cursor: ownerRemoveLoading ? 'not-allowed' : 'pointer', minHeight: '28px' }}
                          >
                            {ownerRemoveLoading === String(t._id) ? '…' : 'Remove'}
                          </button>
                        </div>
                      ))
                }
                {allTenants.filter(t => !ownerTenants.some(o => String(o._id) === String(t._id))).length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <select
                      value={addCentreId}
                      onChange={e => setAddCentreId(e.target.value)}
                      style={{ flex: 1, padding: '8px 10px', borderRadius: '7px', border: `1px solid ${colors.borderDefault}`, fontSize: '12px', color: colors.textPrimary, minHeight: '36px' }}
                    >
                      <option value="">Add a centre…</option>
                      {allTenants
                        .filter(t => !ownerTenants.some(o => String(o._id) === String(t._id)))
                        .map(t => <option key={t._id} value={t._id}>{t.tenantName}{t.city ? ` · ${t.city}` : ''}</option>)
                      }
                    </select>
                    <button
                      type="button"
                      disabled={!addCentreId || addCentreLoading}
                      onClick={handleAddOwnerTenant}
                      style={{ padding: '8px 14px', borderRadius: '7px', border: 'none', background: (addCentreId && !addCentreLoading) ? colors.brandPrimary : colors.textMuted, color: '#fff', fontSize: '12px', fontWeight: '700', cursor: (addCentreId && !addCentreLoading) ? 'pointer' : 'default', minHeight: '36px', whiteSpace: 'nowrap' }}
                    >
                      {addCentreLoading ? '…' : 'Assign'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {editForm.role === 'patient' && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Assign to Centre</div>
                {editUser.tenantId ? (
                  <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '8px' }}>
                    Already assigned to <strong>{editUser.tenantName || editUser.tenantId}</strong>
                  </div>
                ) : null}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={assignTenantId}
                    onChange={e => { setAssignTenantId(e.target.value); setAssignError(null); setAssignSuccess(null); }}
                    style={{ flex: 1, padding: '8px 10px', borderRadius: '7px', border: `1px solid ${colors.borderDefault}`, fontSize: '12px', color: colors.textPrimary, minHeight: '36px' }}
                    disabled={assignLoading}
                  >
                    <option value="">Select centre…</option>
                    {allTenants.map(t => (
                      <option key={t._id} value={t._id}>{t.tenantName}{t.city ? ` · ${t.city}` : ''}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={!assignTenantId || assignLoading}
                    onClick={handleAssignPatient}
                    style={{ padding: '8px 14px', borderRadius: '7px', border: 'none', background: (assignTenantId && !assignLoading) ? colors.brandPrimary : colors.textMuted, color: '#fff', fontSize: '12px', fontWeight: '700', cursor: (assignTenantId && !assignLoading) ? 'pointer' : 'default', minHeight: '36px', whiteSpace: 'nowrap' }}
                  >
                    {assignLoading ? '…' : 'Assign'}
                  </button>
                </div>
                {assignError && <p style={{ color: colors.statusDanger, fontSize: '12px', marginTop: '6px', marginBottom: 0 }}>{assignError}</p>}
                {assignSuccess && <p style={{ color: colors.statusSuccess, fontSize: '12px', marginTop: '6px', marginBottom: 0 }}>{assignSuccess}</p>}
              </div>
            )}

            {dropRemarksUser && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '380px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: colors.textPrimary, marginBottom: '4px' }}>Drop healer from centre?</div>
                  <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '16px', lineHeight: '1.5' }}>
                    {dropRemarksUser.centreName} — a 90-day cooldown will start. Cannot be re-invited until expired unless overridden.
                  </div>
                  <textarea
                    value={dropRemarksText}
                    onChange={e => setDropRemarksText(e.target.value)}
                    placeholder="Reason for dropping (min 10 chars)…"
                    rows={3}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: '7px', border: `1px solid ${colors.borderDefault}`, fontSize: '13px', resize: 'vertical', marginBottom: '16px' }}
                  />
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setDropRemarksUser(null)} style={{ padding: '10px 18px', borderRadius: '7px', border: `1px solid ${colors.borderDefault}`, background: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>Cancel</button>
                    <button
                      type="button"
                      disabled={dropRemarksText.trim().length < 10 || !!crRemoveLoading}
                      onClick={handleSoftDropHealer}
                      style={{ padding: '10px 18px', borderRadius: '7px', border: 'none', background: dropRemarksText.trim().length >= 10 ? colors.statusDanger : colors.borderDefault, color: '#fff', fontSize: '13px', fontWeight: '700', cursor: dropRemarksText.trim().length >= 10 && !crRemoveLoading ? 'pointer' : 'not-allowed', minHeight: '44px' }}
                    >
                      {crRemoveLoading ? '…' : 'Confirm Drop'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {crConfirm && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '380px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>{crConfirm.title}</div>
                  <div style={{ fontSize: '13px', color: '#555', marginBottom: '20px', lineHeight: '1.5' }}>{crConfirm.body}</div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setCrConfirm(null)} style={{ padding: '10px 18px', borderRadius: '7px', border: '1px solid #ddd', background: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>Cancel</button>
                    <button type="button" onClick={crConfirm.onConfirm} disabled={!!crRemoveLoading || addCrOpLoading} style={{ padding: '10px 18px', borderRadius: '7px', border: 'none', background: '#e74c3c', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: (crRemoveLoading || addCrOpLoading) ? 'not-allowed' : 'pointer', minHeight: '44px' }}>{crConfirm.confirmLabel}</button>
                  </div>
                </div>
              </div>
            )}

            {ownerConfirm && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '380px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>{ownerConfirm.title}</div>
                  <div style={{ fontSize: '13px', color: '#555', marginBottom: '20px', lineHeight: '1.5' }}>{ownerConfirm.body}</div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setOwnerConfirm(null)} style={{ padding: '10px 18px', borderRadius: '7px', border: '1px solid #ddd', background: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>Cancel</button>
                    <button type="button" onClick={ownerConfirm.onConfirm} disabled={!!ownerRemoveLoading || addCentreLoading} style={{ padding: '10px 18px', borderRadius: '7px', border: 'none', background: '#e74c3c', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: (ownerRemoveLoading || addCentreLoading) ? 'not-allowed' : 'pointer', minHeight: '44px' }}>{ownerConfirm.confirmLabel}</button>
                  </div>
                </div>
              </div>
            )}

            {assignConfirm && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '380px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>Assign patient to centre?</div>
                  <div style={{ fontSize: '13px', color: '#555', marginBottom: '20px', lineHeight: '1.5' }}>
                    Assign <strong>{editUser.firstName} {editUser.lastName}</strong> to <strong>{assignConfirm.tenantName}</strong>? This will create a patient record at that centre.
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setAssignConfirm(null)} style={{ padding: '10px 18px', borderRadius: '7px', border: '1px solid #ddd', background: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>Cancel</button>
                    <button type="button" onClick={assignConfirm.onConfirm} style={{ padding: '10px 18px', borderRadius: '7px', border: 'none', background: colors.brandPrimary, color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>Confirm Assign</button>
                  </div>
                </div>
              </div>
            )}
            {editError && <p style={{ ...S.errorText, marginBottom: '12px' }}>{editError}</p>}

            <div style={S.modalFooter}>
              <button
                style={S.cancelBtn}
                onClick={handleCloseEdit}
                type="button"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                style={S.saveBtn}
                onClick={handleSaveEdit}
                type="button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreateOwner && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '28px',
            width: '100%', maxWidth: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', margin: '16px',
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '17px', fontWeight: '700' }}>
              Create Owner Account
            </h3>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
              First Name *
            </label>
            <input
              value={ownerForm.firstName}
              onChange={e => setOwnerForm(f => ({ ...f, firstName: e.target.value }))}
              placeholder="e.g. Ramesh"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: '1px solid #ddd', fontSize: '14px', marginBottom: '14px',
                boxSizing: 'border-box',
              }}
            />
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
              Last Name (optional)
            </label>
            <input
              value={ownerForm.lastName}
              onChange={e => setOwnerForm(f => ({ ...f, lastName: e.target.value }))}
              placeholder="e.g. Kumar"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: '1px solid #ddd', fontSize: '14px', marginBottom: '14px',
                boxSizing: 'border-box',
              }}
            />
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
              Email Address *
            </label>
            <input
              type="email"
              value={ownerForm.email}
              onChange={e => setOwnerForm(f => ({ ...f, email: e.target.value }))}
              placeholder="e.g. ramesh@example.com"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: '1px solid #ddd', fontSize: '14px', marginBottom: '6px',
                boxSizing: 'border-box',
              }}
            />
            {ownerError && (
              <p style={{ color: colors.statusDanger, fontSize: '13px', margin: '0 0 12px' }}>
                {ownerError}
              </p>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button
                type="button"
                onClick={() => setShowCreateOwner(false)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px',
                  border: '1px solid #ddd', background: '#fff',
                  fontSize: '14px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateOwner}
                disabled={ownerSubmitting}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                  background: ownerSubmitting ? '#ccc' : colors.brandPrimary,
                  color: '#fff', fontSize: '14px', fontWeight: '600',
                  cursor: ownerSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {ownerSubmitting ? 'Creating…' : 'Create Owner'}
              </button>
            </div>
          </div>
        </div>
      )}
      {assignOwnerConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '380px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', margin: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>Promote to Owner?</div>
            <div style={{ fontSize: '13px', color: '#555', marginBottom: '20px', lineHeight: '1.6' }}>
              <strong>{assignOwnerConfirm.user.firstName} {assignOwnerConfirm.user.lastName}</strong> will become an Owner.
              Their current role (<strong>{assignOwnerConfirm.user.role}</strong>) will be replaced.
              You can assign centres to them from the Edit modal afterward.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setAssignOwnerConfirm(null)}
                disabled={assignOwnerLoading}
                style={{ padding: '10px 18px', borderRadius: '7px', border: '1px solid #ddd', background: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={assignOwnerConfirm.onConfirm}
                disabled={assignOwnerLoading}
                style={{ padding: '10px 18px', borderRadius: '7px', border: 'none', background: colors.brandPrimary, color: '#fff', fontSize: '13px', fontWeight: '700', cursor: assignOwnerLoading ? 'not-allowed' : 'pointer', minHeight: '44px' }}
              >
                {assignOwnerLoading ? 'Promoting…' : 'Promote to Owner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
